import type { ProgramFixedContentRow } from '../types/domain';
import { buildAnnualCellKey } from './annualTimetable';

/** 연간진도표 `unit_text` 저장값 → 표시용 문자열 (ProgramProgressTable 과 동일 규칙) */

function joinUnitNoteLines(raw: string): string {
  return (raw ?? '')
    .split('\n')
    .map((v) => v.trim())
    .filter(Boolean)
    .join('\n');
}

function serializeUnitStorage(textRaw: string, noteRaw: string, joinNoteLines: (s: string) => string): string {
  const text = (textRaw ?? '').trim();
  const note = joinNoteLines(noteRaw ?? '');
  if (!text && !note) return '';
  if (!note) return text;
  return JSON.stringify({ text, note });
}

/** 기본 학습내용 등록의 단원 본문·성취기준 → 프로그램 단원 셀 저장값(JSON 본문 + note) */
export function serializeProgramUnitCellFromLearningContentAdmin(
  unitPlainText: string,
  achievementPlainText: string
): string {
  return serializeUnitStorage(unitPlainText ?? '', achievementPlainText ?? '', joinUnitNoteLines);
}

function migrateUnitCellFromDbToCanonicalStored(raw: string): string {
  const t = (raw ?? '').trim();
  if (!t) return '';
  if (!t.startsWith('{')) {
    return serializeUnitStorage(t, '', joinUnitNoteLines);
  }
  try {
    const o = JSON.parse(t) as Record<string, unknown>;
    const text =
      (typeof o.text === 'string' ? o.text : typeof o.name === 'string' ? o.name : '')?.trim() ?? '';
    const noteRaw =
      typeof o.note === 'string' ? o.note : typeof o.criterion === 'string' ? o.criterion : '';
    return serializeUnitStorage(text, noteRaw, joinUnitNoteLines);
  } catch {
    return serializeUnitStorage(t, '', joinUnitNoteLines);
  }
}

function parseUnitStorageRaw(raw: string): { text: string; note: string } {
  const t = (raw ?? '').trim();
  if (!t) return { text: '', note: '' };
  if (!t.startsWith('{')) return { text: t, note: '' };
  try {
    const o = JSON.parse(t) as { text?: unknown; note?: unknown };
    if (typeof o !== 'object' || o === null) return { text: t, note: '' };
    const text = typeof o.text === 'string' ? o.text.trim() : '';
    const note = typeof o.note === 'string' ? o.note.trim() : '';
    return { text, note };
  } catch {
    return { text: t, note: '' };
  }
}

/** 단원 저장값 → 본문·note (연간진도표·툴팁 등) */
export function getUnitTextAndNoteForDisplay(raw: string): { text: string; note: string } {
  const migrated = migrateUnitCellFromDbToCanonicalStored(raw);
  return parseUnitStorageRaw(migrated);
}

/** 기록 모아보기·보내기 등: 단원 본문만(성취기준·단원 메모 note 제외) */
export function formatUnitTextForDisplay(raw: string): string {
  return getUnitTextAndNoteForDisplay(raw).text;
}

export function formatLearningTextForDisplay(raw: string): string {
  return parseMemoFieldStorageDeep(raw ?? '').text.replace(/\r\n/g, '\n').trim();
}

export function serializeCrossStorage(textRaw: string, noteRaw: string): string {
  const text = (textRaw ?? '').trim();
  const note = (noteRaw ?? '').trim();
  if (!text && !note) return '';
  if (!note) return text;
  return JSON.stringify({ text, note });
}

function migrateCrossCellFromDbToCanonicalStored(raw: string): string {
  const t = (raw ?? '').trim();
  if (!t) return '';
  if (!t.startsWith('{')) {
    return serializeCrossStorage(t, '');
  }
  try {
    const o = JSON.parse(t) as Record<string, unknown>;
    const text =
      (typeof o.text === 'string' ? o.text : typeof o.topic === 'string' ? o.topic : '')?.trim() ?? '';
    const note =
      (typeof o.note === 'string' ? o.note : typeof o.content === 'string' ? o.content : '')?.trim() ?? '';
    return serializeCrossStorage(text, note);
  } catch {
    return serializeCrossStorage(t, '');
  }
}

function parseCrossStorageRaw(raw: string): { text: string; note: string } {
  const t = (raw ?? '').trim();
  if (!t) return { text: '', note: '' };
  if (!t.startsWith('{')) return { text: t, note: '' };
  try {
    const o = JSON.parse(t) as { text?: unknown; note?: unknown };
    if (typeof o !== 'object' || o === null) return { text: t, note: '' };
    const text = typeof o.text === 'string' ? o.text.trim() : '';
    const note = typeof o.note === 'string' ? o.note.trim() : '';
    return { text, note };
  } catch {
    return { text: t, note: '' };
  }
}

/** 범교과 저장값 → 주제어·note (연간진도표·툴팁 등) */
export function getCrossTopicAndNoteForDisplay(raw: string): { text: string; note: string } {
  const migrated = migrateCrossCellFromDbToCanonicalStored(raw);
  return parseCrossStorageRaw(migrated);
}

/** 범교과 저장값 → 표시용 주제어(본문). ProgramProgressTable 과 동일 */
export function formatCrossCurricularTopicForDisplay(raw: string): string {
  return getCrossTopicAndNoteForDisplay(raw).text;
}

function uuidLikeFromUnknown(value: unknown): string {
  if (value == null) return '';
  const s = typeof value === 'string' ? value.trim() : String(value).trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ? s : '';
}

/**
 * JSON(최대 5중) 해제 후 본문·상세·행동특성.
 * `behavior_note`는 최외곽 객체에서만 읽음(연간 행동특성·진도표 수업기록 공통 저장).
 */
/** 수업기록(lesson)·평가기록(eval_log)별 AI 피드백 텍스트(#m: 태그 포함) */
export type RecordAiFeedbackMap = { lesson?: string; eval_log?: string };

export function normalizeRecordAiFeedbackMap(value: unknown): RecordAiFeedbackMap | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Record<string, unknown>;
  const out: RecordAiFeedbackMap = {};
  if (typeof v.lesson === 'string' && v.lesson.trim()) out.lesson = v.lesson;
  if (typeof v.eval_log === 'string' && v.eval_log.trim()) out.eval_log = v.eval_log;
  return out.lesson || out.eval_log ? out : undefined;
}

export function parseMemoFieldStorageDeep(raw: string): {
  text: string;
  note: string;
  behaviorNote: string;
  presentationRefId?: string;
  worksheetRefId?: string;
  evaluationPlanAppliedId?: string;
  evaluationPlanSummary?: string;
  evaluationPlanElement?: string;
  evaluationPlanMethod?: string;
  /** 수업·평가기록에 연결된 과제 체크 item id (원본 메모는 과제 쪽) */
  assignmentMemoItemId?: string;
  /** 연결 과제 제목 캐시(표시용) */
  assignmentMemoItemTitle?: string;
  /** 연결 시 선택한 멤버 그룹(학급). 없으면 전체 */
  assignmentMemoGroupId?: string;
  /** 연결 그룹 이름 캐시(표시용) */
  assignmentMemoGroupTitle?: string;
  /** 기록에 내장된 AI 분석·피드백(기록과 함께 이동·저장·삭제) */
  aiFeedback?: RecordAiFeedbackMap;
} {
  const initial = (raw ?? '').trim();
  if (!initial) return { text: '', note: '', behaviorNote: '' };

  let text = initial;
  let note = '';
  let behaviorNote = '';
  let presentationRefId = '';
  let worksheetRefId = '';
  let evaluationPlanAppliedId = '';
  let evaluationPlanSummary = '';
  let evaluationPlanElement = '';
  let evaluationPlanMethod = '';
  let assignmentMemoItemId = '';
  let assignmentMemoItemTitle = '';
  let assignmentMemoGroupId = '';
  let assignmentMemoGroupTitle = '';
  let aiFeedback: RecordAiFeedbackMap | undefined;

  const absorbPresentationRefId = (value: unknown) => {
    if (presentationRefId) return;
    const u = uuidLikeFromUnknown(value);
    if (u) presentationRefId = u;
  };

  const absorbWorksheetRefId = (value: unknown) => {
    if (worksheetRefId) return;
    const u = uuidLikeFromUnknown(value);
    if (u) worksheetRefId = u;
  };

  const absorbEvaluationPlanAppliedId = (value: unknown) => {
    if (evaluationPlanAppliedId) return;
    const u = uuidLikeFromUnknown(value);
    if (u) evaluationPlanAppliedId = u;
  };

  const absorbEvaluationPlanSummary = (value: unknown) => {
    if (evaluationPlanSummary) return;
    if (typeof value === 'string' && value.trim()) evaluationPlanSummary = value.trim();
  };

  const absorbEvaluationPlanElement = (value: unknown) => {
    if (evaluationPlanElement) return;
    if (typeof value === 'string' && value.trim()) evaluationPlanElement = value.trim();
  };

  const absorbEvaluationPlanMethod = (value: unknown) => {
    if (evaluationPlanMethod) return;
    if (typeof value === 'string' && value.trim()) evaluationPlanMethod = value.trim();
  };

  const absorbAssignmentMemoItemId = (value: unknown) => {
    if (assignmentMemoItemId) return;
    if (typeof value === 'string' && value.trim()) assignmentMemoItemId = value.trim();
  };

  const absorbAssignmentMemoItemTitle = (value: unknown) => {
    if (assignmentMemoItemTitle) return;
    if (typeof value === 'string' && value.trim()) assignmentMemoItemTitle = value.trim();
  };

  const absorbAssignmentMemoGroupId = (value: unknown) => {
    if (assignmentMemoGroupId) return;
    if (typeof value === 'string' && value.trim()) assignmentMemoGroupId = value.trim();
  };

  const absorbAssignmentMemoGroupTitle = (value: unknown) => {
    if (assignmentMemoGroupTitle) return;
    if (typeof value === 'string' && value.trim()) assignmentMemoGroupTitle = value.trim();
  };

  for (let i = 0; i < 5; i += 1) {
    const candidate = text.trim();
    if (!candidate.startsWith('{')) break;
    try {
      const parsed = JSON.parse(candidate) as {
        text?: unknown;
        note?: unknown;
        behavior_note?: unknown;
      };
      if (i === 0 && typeof parsed?.behavior_note === 'string') {
        behaviorNote = parsed.behavior_note.trim();
      }
      absorbPresentationRefId((parsed as { presentationRefId?: unknown }).presentationRefId);
      absorbPresentationRefId((parsed as { presentation_ref_id?: unknown }).presentation_ref_id);
      absorbPresentationRefId((parsed as { presentationRef?: unknown }).presentationRef);
      absorbPresentationRefId((parsed as { presentation_ref?: unknown }).presentation_ref);
      absorbWorksheetRefId((parsed as { worksheetRefId?: unknown }).worksheetRefId);
      absorbWorksheetRefId((parsed as { worksheet_ref_id?: unknown }).worksheet_ref_id);
      absorbWorksheetRefId((parsed as { worksheetRef?: unknown }).worksheetRef);
      absorbWorksheetRefId((parsed as { worksheet_ref?: unknown }).worksheet_ref);
      absorbEvaluationPlanAppliedId((parsed as { evaluationPlanAppliedId?: unknown }).evaluationPlanAppliedId);
      absorbEvaluationPlanAppliedId((parsed as { evaluation_plan_applied_id?: unknown }).evaluation_plan_applied_id);
      absorbEvaluationPlanSummary((parsed as { evaluationPlanSummary?: unknown }).evaluationPlanSummary);
      absorbEvaluationPlanSummary((parsed as { evaluation_plan_summary?: unknown }).evaluation_plan_summary);
      absorbEvaluationPlanElement((parsed as { evaluationPlanElement?: unknown }).evaluationPlanElement);
      absorbEvaluationPlanElement((parsed as { evaluation_plan_element?: unknown }).evaluation_plan_element);
      absorbEvaluationPlanMethod((parsed as { evaluationPlanMethod?: unknown }).evaluationPlanMethod);
      absorbEvaluationPlanMethod((parsed as { evaluation_plan_method?: unknown }).evaluation_plan_method);
      absorbAssignmentMemoItemId((parsed as { assignmentMemoItemId?: unknown }).assignmentMemoItemId);
      absorbAssignmentMemoItemId((parsed as { assignment_memo_item_id?: unknown }).assignment_memo_item_id);
      absorbAssignmentMemoItemTitle((parsed as { assignmentMemoItemTitle?: unknown }).assignmentMemoItemTitle);
      absorbAssignmentMemoItemTitle((parsed as { assignment_memo_item_title?: unknown }).assignment_memo_item_title);
      absorbAssignmentMemoGroupId((parsed as { assignmentMemoGroupId?: unknown }).assignmentMemoGroupId);
      absorbAssignmentMemoGroupId((parsed as { assignment_memo_group_id?: unknown }).assignment_memo_group_id);
      absorbAssignmentMemoGroupTitle((parsed as { assignmentMemoGroupTitle?: unknown }).assignmentMemoGroupTitle);
      absorbAssignmentMemoGroupTitle((parsed as { assignment_memo_group_title?: unknown }).assignment_memo_group_title);
      if (!aiFeedback) aiFeedback = normalizeRecordAiFeedbackMap((parsed as { ai_feedback?: unknown }).ai_feedback);
      const nextText = typeof parsed?.text === 'string' ? parsed.text : '';
      const nextNote = typeof parsed?.note === 'string' ? parsed.note : '';
      if (nextNote && !note) note = nextNote;
      text = nextText;
    } catch {
      break;
    }
  }

  if (!presentationRefId) {
    const loose = initial.match(
      /["']?(?:presentationRefId|presentation_ref_id|presentationRef|presentation_ref)["']?\s*[:=]\s*\\?["']?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\\?["']?/i
    );
    if (loose?.[1]) presentationRefId = loose[1];
  }

  if (!worksheetRefId) {
    const loose = initial.match(
      /["']?(?:worksheetRefId|worksheet_ref_id|worksheetRef|worksheet_ref)["']?\s*[:=]\s*\\?["']?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\\?["']?/i
    );
    if (loose?.[1]) worksheetRefId = loose[1];
  }

  if (!evaluationPlanAppliedId) {
    const loose = initial.match(
      /["']?(?:evaluationPlanAppliedId|evaluation_plan_applied_id)["']?\s*[:=]\s*\\?["']?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\\?["']?/i
    );
    if (loose?.[1]) evaluationPlanAppliedId = loose[1];
  }

  if ((!evaluationPlanElement || !evaluationPlanMethod) && evaluationPlanSummary) {
    const fromSummary = resolveEvaluationPlanMemoAutoLines({
      evaluationPlanElement,
      evaluationPlanMethod,
      evaluationPlanSummary,
    });
    if (!evaluationPlanElement) evaluationPlanElement = fromSummary.element;
    if (!evaluationPlanMethod) evaluationPlanMethod = fromSummary.method;
  }

  return {
    text: text.trim(),
    note: note.trim(),
    behaviorNote,
    ...(presentationRefId ? { presentationRefId } : {}),
    ...(worksheetRefId ? { worksheetRefId } : {}),
    ...(evaluationPlanAppliedId ? { evaluationPlanAppliedId } : {}),
    ...(evaluationPlanSummary ? { evaluationPlanSummary } : {}),
    ...(evaluationPlanElement ? { evaluationPlanElement } : {}),
    ...(evaluationPlanMethod ? { evaluationPlanMethod } : {}),
    ...(assignmentMemoItemId ? { assignmentMemoItemId } : {}),
    ...(assignmentMemoItemTitle ? { assignmentMemoItemTitle } : {}),
    ...(assignmentMemoGroupId ? { assignmentMemoGroupId } : {}),
    ...(assignmentMemoGroupTitle ? { assignmentMemoGroupTitle } : {}),
    ...(aiFeedback ? { aiFeedback } : {}),
  };
}

/** 평가계획 연결 시 메모 상단에 고정 표시할 평가요소·평가방법 */
export function resolveEvaluationPlanMemoAutoLines(params: {
  evaluationPlanElement?: string | null;
  evaluationPlanMethod?: string | null;
  evaluationPlanSummary?: string | null;
}): { element: string; method: string } {
  let element = (params.evaluationPlanElement ?? '').trim();
  let method = (params.evaluationPlanMethod ?? '').trim();
  if ((!element || !method) && (params.evaluationPlanSummary ?? '').trim()) {
    const parts = (params.evaluationPlanSummary ?? '')
      .split(' / ')
      .map((p) => p.trim())
      .filter(Boolean);
    // formatPlanSummary: `단원 / 평가요소 / 평가방법`
    if (parts.length >= 3) {
      if (!element) element = parts[1] ?? '';
      if (!method) method = parts[2] ?? '';
    } else if (parts.length === 2) {
      if (!element) element = parts[0] ?? '';
      if (!method) method = parts[1] ?? '';
    }
  }
  return { element, method };
}

export function formatEvaluationPlanMemoAutoText(params: {
  evaluationPlanElement?: string | null;
  evaluationPlanMethod?: string | null;
  evaluationPlanSummary?: string | null;
}): string {
  const { element, method } = resolveEvaluationPlanMemoAutoLines(params);
  return [element, method].filter(Boolean).join('\n');
}

export function composeEvaluationMemoDisplayNote(
  userNote: string,
  planFields: {
    evaluationPlanAppliedId?: string | null;
    evaluationPlanElement?: string | null;
    evaluationPlanMethod?: string | null;
    evaluationPlanSummary?: string | null;
  }
): string {
  const hasPlan = !!(planFields.evaluationPlanAppliedId ?? '').trim();
  const autoText = hasPlan ? formatEvaluationPlanMemoAutoText(planFields) : '';
  const note = (userNote ?? '').trim();
  if (autoText && note) return `${autoText}\n\n${note}`;
  return autoText || note;
}

/** 동기화 미러·워크스페이스 병합 후 대표 행의 presentation/worksheet ref를 보존 */
export function mergeProgramLearningTextAttachmentRefs(
  primaryRaw: string | null | undefined,
  supplementalRaw: string | null | undefined
): string {
  const primary = (primaryRaw ?? '').trim();
  const supplemental = (supplementalRaw ?? '').trim();
  if (!supplemental) return primary;
  if (!primary) return supplemental;

  const p = parseMemoFieldStorageDeep(primary);
  const s = parseMemoFieldStorageDeep(supplemental);
  const supplementalIsJson = supplemental.startsWith('{');

  const text = supplementalIsJson ? (s.text.trim() || p.text) : supplemental || s.text || p.text;
  /** 평문 patch는 본문만 갱신 — baseline JSON의 note/behavior를 끌어오지 않는다 */
  const note = supplementalIsJson ? (s.note.trim() || p.note) : '';
  const behaviorNote = supplementalIsJson ? (s.behaviorNote.trim() || p.behaviorNote) : '';
  const presentationRefId = s.presentationRefId || p.presentationRefId || '';
  const worksheetRefId = s.worksheetRefId || p.worksheetRefId || '';

  if (!text.trim() && !note.trim() && !presentationRefId && !worksheetRefId && !behaviorNote.trim()) {
    return '';
  }
  if (!note.trim() && !presentationRefId && !worksheetRefId && !behaviorNote.trim()) {
    return text;
  }

  const payload: {
    text: string;
    note?: string;
    behavior_note?: string;
    presentationRefId?: string;
    worksheetRefId?: string;
  } = { text };
  if (note.trim()) payload.note = note;
  if (behaviorNote.trim()) payload.behavior_note = behaviorNote;
  if (presentationRefId) payload.presentationRefId = presentationRefId;
  if (worksheetRefId) payload.worksheetRefId = worksheetRefId;
  return JSON.stringify(payload);
}

/** draft·cell·overlay·동기화 소스에서 presentation/worksheet ref·note를 한 learning_text로 합친다 */
export function mergeProgramLearningTextAttachmentRefsFromSources(
  ...sources: Array<string | null | undefined>
): string {
  let merged = '';
  for (const src of sources) {
    if (!(src ?? '').trim()) continue;
    merged = mergeProgramLearningTextAttachmentRefs(merged, src);
  }
  return merged;
}

/** ProgramProgressTable·연간진도 표시용: JSON 중첩 해제 후 본문·상세 */
export function parseProgramProgressMemoJsonForDisplay(raw: string): { text: string; note: string } {
  const { text, note } = parseMemoFieldStorageDeep(raw);
  return { text, note };
}

/** 수업기록 셀 저장: 수업·평가 메모와 행동특성(behavior_note)·과제메모 링크 병합 */
export function serializeClassRecordCellStorage(
  textRaw: string,
  noteRaw: string,
  behaviorNoteRaw?: string | null,
  assignmentMemoItemIdRaw?: string | null,
  assignmentMemoItemTitleRaw?: string | null,
  assignmentMemoGroupIdRaw?: string | null,
  assignmentMemoGroupTitleRaw?: string | null,
  aiFeedbackRaw?: RecordAiFeedbackMap | null
): string {
  const text = (textRaw ?? '').trim();
  const note = noteRaw ?? '';
  const b = (behaviorNoteRaw ?? '').trim();
  const assignmentMemoItemId = (assignmentMemoItemIdRaw ?? '').trim();
  const assignmentMemoItemTitle = (assignmentMemoItemTitleRaw ?? '').trim();
  const assignmentMemoGroupId = (assignmentMemoGroupIdRaw ?? '').trim();
  const assignmentMemoGroupTitle = (assignmentMemoGroupTitleRaw ?? '').trim();
  if (!text && !note.trim() && !b && !assignmentMemoItemId) return '';
  // 기록이 없으면 피드백도 저장하지 않음(삭제 시 함께 제거)
  const aiFeedback = text || note.trim() ? normalizeRecordAiFeedbackMap(aiFeedbackRaw) : undefined;
  if (!note.trim() && !b && !assignmentMemoItemId && !aiFeedback) return text;
  const o: {
    text: string;
    note?: string;
    behavior_note?: string;
    assignmentMemoItemId?: string;
    assignmentMemoItemTitle?: string;
    assignmentMemoGroupId?: string;
    assignmentMemoGroupTitle?: string;
    ai_feedback?: RecordAiFeedbackMap;
  } = { text };
  if (note.trim()) o.note = note;
  if (b) o.behavior_note = b;
  if (assignmentMemoItemId) {
    o.assignmentMemoItemId = assignmentMemoItemId;
    if (assignmentMemoItemTitle) o.assignmentMemoItemTitle = assignmentMemoItemTitle;
    if (assignmentMemoGroupId) {
      o.assignmentMemoGroupId = assignmentMemoGroupId;
      if (assignmentMemoGroupTitle) o.assignmentMemoGroupTitle = assignmentMemoGroupTitle;
    }
  }
  if (aiFeedback) o.ai_feedback = aiFeedback;
  return JSON.stringify(o);
}

/** 사용자 메모 + 연결된 과제 이름태그 메모를 모아보기·미리보기용으로 합침 */
export function composeClassRecordNoteWithLinkedAssignmentMemo(
  userNote: string,
  linkedAssignmentMemo: string
): string {
  const a = (userNote ?? '').trim();
  const b = (linkedAssignmentMemo ?? '').trim();
  if (a && b) return `${a}\n\n${b}`;
  return a || b;
}

const EVAL_STATUS_LABELS: Record<string, string> = {
  '1': '평가계획',
  '2': '평가중',
  '3': '평가완료',
};

export function formatEvaluationCodeForDisplay(raw: string): string {
  const v = parseProgramProgressMemoJsonForDisplay(raw ?? '').text.trim();
  if (!v) return '';
  return EVAL_STATUS_LABELS[v] ?? v;
}

export function formatClassRecordCodeForDisplay(raw: string): string {
  const { text } = parseMemoFieldStorageDeep(raw ?? '');
  const v = text.trim();
  if (v === 'lesson') return '수업기록';
  if (v === 'eval_log') return '평가기록';
  if (v) return v;
  return '';
}

function normalizeFixedEventNameForStorage(raw: string): string {
  return (raw ?? '').trim().replace(/^◆+/, '').replace(/-+$/, '').trim();
}

function escapeRegExpForFixedDiamond(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * ProgramProgressTable `stripConflictingFixedLearningDiamondPrefixes` 와 동일:
 * 일정 행사명에 `-`가 있어도 `◆{행사}-` 접두만 반복 제거한 뒤 잔여 잘못된 ◆…- 한 덩어리를 정리한다.
 */
function stripConflictingFixedLearningPlainDiamondPrefixes(raw: string, eventNorm: string): string {
  const ev = normalizeFixedEventNameForStorage(eventNorm);
  let out = (raw ?? '').trim();
  if (ev) {
    const re = new RegExp(`^◆\\s*${escapeRegExpForFixedDiamond(ev)}\\s*-`);
    while (re.test(out)) out = out.replace(re, '');
  }
  return out.replace(/^◆[^-]*-?/, '');
}

export function applyFixedLearningPrefixPlain(eventNorm: string, plain: string): string {
  if (!eventNorm) return plain;
  const prefix = `◆${eventNorm}-`;
  const raw = (plain ?? '').trim();
  if (!raw) return prefix;
  const stripped = stripConflictingFixedLearningPlainDiamondPrefixes(raw, eventNorm);
  return `${prefix}${stripped}`;
}

/** 일정 memo/시간표명이 비어 있을 때(비수업 등) 저장된 고정 프로그램 행에서 이벤트명을 복원 */
function resolveFixedEventNameForContentRow(row: ProgramFixedContentRow): string {
  const fromEvent = normalizeFixedEventNameForStorage(row.event_name ?? '');
  if (fromEvent) return fromEvent;
  const unitMain = getUnitTextAndNoteForDisplay(row.unit_text ?? '').text.trim();
  if (unitMain) {
    const fromUnit = normalizeFixedEventNameForStorage(unitMain);
    if (fromUnit) return fromUnit;
  }
  return fixedLearningPlainPrefixEventName(row.learning_text ?? '');
}

/**
 * 고정 프로그램 일정 칸: 연간시간표 행사명(memo/시간표 텍스트)을 단원 `◆이름`·학습 `◆이름-` 접두의 기준으로 맞춘다.
 * 스케줄 재배치 후에도 `source_annual_cell_key`를 현재 칸 키로 두고 접두·단원을 일정과 동기화한다.
 * 비수업처럼 일정 행사명이 비어도 저장된 고정 프로그램 내용에서 이벤트명을 보완한다.
 */
export function alignFixedContentRowToScheduleSlot(
  scheduleAnnualKey: string,
  scheduleEventDisplayRaw: string,
  row: ProgramFixedContentRow
): ProgramFixedContentRow {
  const scheduleEvt = normalizeFixedEventNameForStorage(scheduleEventDisplayRaw);
  const resolvedEvt = scheduleEvt || resolveFixedEventNameForContentRow(row);
  const unitText = resolvedEvt ? `◆${resolvedEvt}` : (row.unit_text ?? '');
  let learningText = row.learning_text ?? '';
  if (resolvedEvt) {
    const raw = learningText.replace(/\r\n/g, '\n').trim();
    if (!raw) {
      learningText = applyFixedLearningPrefixPlain(resolvedEvt, '');
    } else if (raw.startsWith('{')) {
      const deep = parseMemoFieldStorageDeep(raw);
      const inner = applyFixedLearningPrefixPlain(resolvedEvt, deep.text);
      if (!inner && !deep.note.trim() && !deep.behaviorNote.trim() && !deep.presentationRefId && !deep.worksheetRefId) {
        learningText = '';
      } else if (!deep.note.trim() && !deep.behaviorNote.trim() && !deep.presentationRefId && !deep.worksheetRefId) {
        learningText = inner;
      } else {
        const o: {
          text: string;
          note?: string;
          behavior_note?: string;
          presentationRefId?: string;
          worksheetRefId?: string;
        } = { text: inner };
        if (deep.note.trim()) o.note = deep.note;
        if (deep.behaviorNote.trim()) o.behavior_note = deep.behaviorNote;
        if (deep.presentationRefId) o.presentationRefId = deep.presentationRefId;
        if (deep.worksheetRefId) o.worksheetRefId = deep.worksheetRefId;
        learningText = JSON.stringify(o);
      }
    } else {
      const stripped = stripConflictingFixedLearningPlainDiamondPrefixes(raw, resolvedEvt);
      learningText = applyFixedLearningPrefixPlain(resolvedEvt, stripped);
    }
  }
  return {
    ...row,
    source_annual_cell_key: scheduleAnnualKey,
    event_name: resolvedEvt || null,
    unit_text: unitText,
    learning_text: learningText,
  };
}

/** `◆이벤트-` 한 줄 본문(또는 JSON 안의 text)에서 이벤트명(정규화). `scheduleHint`가 있으면 행사명에 `-`가 있어도 정확히 매칭한다. */
function plainPrefixEventFromFixedLearningLine(plainLine: string, scheduleHint?: string): string {
  const line = (plainLine ?? '').replace(/\r\n/g, '\n').trim();
  if (!line || line.startsWith('{')) return '';
  const hint = scheduleHint ? normalizeFixedEventNameForStorage(scheduleHint) : '';
  if (hint) {
    const re = new RegExp(`^◆\\s*${escapeRegExpForFixedDiamond(hint)}\\s*-`);
    if (re.test(line)) return hint;
  }
  const m = line.match(/^◆\s*([^-]+?)\s*-/);
  return m?.[1] ? normalizeFixedEventNameForStorage(m[1]) : '';
}

export function fixedLearningPlainPrefixEventName(
  learningTextDb: string | null | undefined,
  scheduleHint?: string
): string {
  const raw = (learningTextDb ?? '').replace(/\r\n/g, '\n').trim();
  if (!raw) return '';
  if (raw.startsWith('{')) {
    const inner = parseMemoFieldStorageDeep(raw).text.trim();
    return plainPrefixEventFromFixedLearningLine(inner, scheduleHint);
  }
  return plainPrefixEventFromFixedLearningLine(raw, scheduleHint);
}

/**
 * 고정 프로그램: DB에 나뉘어 있을 수 있는 단원(`unit_text`)과 학습내용(`learning_text`)을
 * 학습내용 한 줄(◆이벤트-본문, JSON 메모 포함)으로 합친다. 저장·표시 정렬의 단일 근거.
 * 이미 `◆A-…` 형태인데 일정 이벤트가 `B`(A≠B)이면 접두·본문을 일정에 맞춰 갈아끼우지 않는다(한 덩어리 유지).
 */
export function coalesceFixedProgramProgressLearning(
  scheduleEventRaw: string,
  unitTextDb: string | null | undefined,
  learningTextDb: string | null | undefined
): string {
  const scheduleEvt = normalizeFixedEventNameForStorage(scheduleEventRaw);
  const rawLearn = (learningTextDb ?? '').replace(/\r\n/g, '\n');
  const trimmed = rawLearn.trim();
  const unitMain = getUnitTextAndNoteForDisplay(unitTextDb ?? '').text.trim();
  const unitEvt = normalizeFixedEventNameForStorage(unitMain.replace(/^◆+/, '').replace(/-+$/, ''));

  const hintForPrefix = scheduleEvt || unitEvt;

  const lineHasPlainDiamondLearningPrefix = (line: string): boolean => {
    const t = (line ?? '').trim();
    if (!t || t.startsWith('{')) return false;
    if (!t.startsWith('◆')) return false;
    if (scheduleEvt) {
      const re = new RegExp(`^◆\\s*${escapeRegExpForFixedDiamond(scheduleEvt)}\\s*-`);
      if (re.test(t)) return true;
    }
    if (unitEvt && unitEvt !== scheduleEvt) {
      const reU = new RegExp(`^◆\\s*${escapeRegExpForFixedDiamond(unitEvt)}\\s*-`);
      if (reU.test(t)) return true;
    }
    return /^◆\s*[^-]+?-/s.test(t);
  };

  const restamp = (ev: string, body: string): string => {
    const b = (body ?? '').trim();
    if (!b) return applyFixedLearningPrefixPlain(ev, '');
    if (b.startsWith('{')) {
      const deep = parseMemoFieldStorageDeep(body);
      const inner = applyFixedLearningPrefixPlain(ev, deep.text);
      if (!inner && !deep.note.trim() && !deep.behaviorNote.trim() && !deep.presentationRefId && !deep.worksheetRefId) return '';
      if (!deep.note.trim() && !deep.behaviorNote.trim() && !deep.presentationRefId && !deep.worksheetRefId) return inner;
      const o: {
        text: string;
        note?: string;
        behavior_note?: string;
        presentationRefId?: string;
        worksheetRefId?: string;
      } = { text: inner };
      if (deep.note.trim()) o.note = deep.note;
      if (deep.behaviorNote.trim()) o.behavior_note = deep.behaviorNote;
      if (deep.presentationRefId) o.presentationRefId = deep.presentationRefId;
      if (deep.worksheetRefId) o.worksheetRefId = deep.worksheetRefId;
      return JSON.stringify(o);
    }
    return applyFixedLearningPrefixPlain(ev, body);
  };

  const hasPlainPrefix = lineHasPlainDiamondLearningPrefix(trimmed);
  const ev = scheduleEvt || unitEvt;
  if (hasPlainPrefix) {
    const learnEvt = plainPrefixEventFromFixedLearningLine(trimmed, hintForPrefix);
    if (learnEvt && scheduleEvt && learnEvt !== scheduleEvt) {
      return trimmed;
    }
    // 일정 이벤트와 학습 접두가 같으면 restamp 생략(재찍기로 본문이 비는 회귀 방지)
    if (learnEvt && scheduleEvt && learnEvt === scheduleEvt) {
      return trimmed;
    }
    return scheduleEvt ? restamp(scheduleEvt, trimmed) : trimmed;
  }
  if (trimmed.startsWith('{')) {
    const innerEvt = plainPrefixEventFromFixedLearningLine(
      parseMemoFieldStorageDeep(trimmed).text.trim(),
      hintForPrefix
    );
    if (innerEvt && scheduleEvt && innerEvt !== scheduleEvt) {
      return trimmed;
    }
    if (innerEvt && scheduleEvt && innerEvt === scheduleEvt) {
      return trimmed;
    }
    return ev ? restamp(ev, rawLearn) : rawLearn;
  }
  if (!ev) return trimmed;
  return restamp(ev, trimmed);
}

export function normalizeFixedQueueEventKey(raw: string): string {
  return (raw ?? '').trim().replace(/^◆+/, '').replace(/-+$/, '').trim();
}

export type FixedContentLikeForRichness = {
  unit_text?: string | null;
  learning_text?: string | null;
};

/** 동일 이벤트명 큐: 빈 ◆이벤트- 보다 이미 본문이 붙은 학습내용을 먼저 일정에 매칭(ProgramFixedPage·연간시간표 동기화 공통) */
export function fixedSourceRichnessForEventQueue(
  src: FixedContentLikeForRichness,
  queueEventKey: string
): number {
  const ev = normalizeFixedQueueEventKey(queueEventKey);
  if (!ev) return ((src.learning_text ?? '').length + (src.unit_text ?? '').length);
  let learn = (src.learning_text ?? '').replace(/\r\n/g, '\n').trim();
  if (learn.startsWith('{')) {
    learn = parseMemoFieldStorageDeep(learn).text.trim();
  }
  const prefixRe = new RegExp(`^◆\\s*${escapeRegExpForFixedDiamond(ev)}\\s*-`);
  const prefixHit = prefixRe.exec(learn);
  if (prefixHit) {
    const rest = learn.slice(prefixHit[0].length).trim();
    if (rest.length > 0) return 1_000_000 + Math.min(rest.length, 8000);
    return 100;
  }
  const m = learn.match(/^◆\s*([^-]+?)\s*-(.*)$/);
  if (m) {
    const evIn = normalizeFixedQueueEventKey(m[1] ?? '');
    const rest = (m[2] ?? '').trim();
    if (evIn === ev) {
      if (rest.length > 0) return 800_000 + Math.min(rest.length, 8000);
      return 80;
    }
  }
  if (learn.length > 0) return 5000 + Math.min(learn.length, 4000);
  const u = (src.unit_text ?? '').trim();
  if (u && normalizeFixedQueueEventKey(u.replace(/^◆+/, '')) === ev) return 50;
  return 0;
}

/** 동일 이벤트 ◆접두 학습내용에 본문이 있는지(다른 칸의 무관 텍스트는 richness 5000대로 제외) */
export function fixedEventQueueHasSamePrefixLearningBody(
  src: FixedContentLikeForRichness,
  queueEventKey: string
): boolean {
  return fixedSourceRichnessForEventQueue(src, queueEventKey) >= 800_000;
}

/**
 * 고정 프로그램 화면: `program_progress_cells`(legacy)와 정렬된 고정행(src)을 합치기 전에 어느 쪽 단원·학습을 쓸지 고른다.
 * 연간 칸만 바뀌면 legacy는 예전 슬롯 찌거기일 수 있어, `legacy?.learning_text ?? src` 순이면 본문이 바뀐다.
 * 일정 이벤트 키와 학습 `◆접두`가 맞는 쪽을 우선하고, 둘 다 안 맞으면 정렬된 고정행(src)을 쓴다.
 */
export function pickFixedProgramCoalesceInputs(
  scheduleEventRaw: string,
  src: { learning_text?: string | null; unit_text?: string | null },
  legacy?: { learning_text?: string | null; unit_text?: string | null } | null
): { unit_text: string; learning_text: string } {
  const se = normalizeFixedQueueEventKey(scheduleEventRaw);
  const srcL = src.learning_text ?? '';
  const srcU = src.unit_text ?? '';
  const legL = legacy?.learning_text ?? '';
  const legU = legacy?.unit_text ?? '';
  const st = srcL.trim();
  const lt = legL.trim();

  if (!legacy) {
    return { unit_text: srcU, learning_text: srcL };
  }
  if (!lt) return { unit_text: srcU, learning_text: srcL };
  if (!st) return { unit_text: legU, learning_text: legL };

  if (!se) {
    return { unit_text: srcU, learning_text: srcL };
  }

  const srcEv = fixedLearningPlainPrefixEventName(srcL, scheduleEventRaw);
  const legEv = fixedLearningPlainPrefixEventName(legL, scheduleEventRaw);
  const srcOk = srcEv === se;
  const legOk = legEv === se;
  if (srcOk && !legOk) return { unit_text: srcU, learning_text: srcL };
  if (!srcOk && legOk) return { unit_text: legU, learning_text: legL };
  if (srcOk && legOk) {
    const sr = fixedSourceRichnessForEventQueue({ learning_text: srcL, unit_text: srcU }, se);
    const lr = fixedSourceRichnessForEventQueue({ learning_text: legL, unit_text: legU }, se);
    return lr > sr ? { unit_text: legU, learning_text: legL } : { unit_text: srcU, learning_text: srcL };
  }
  return { unit_text: srcU, learning_text: srcL };
}

/** 다른 연간 칸·고정행에 이미 같은 이벤트 접두 학습 본문이 있으면 true (스케줄만 바뀔 때 빈 ◆템플릿 중복 생성 방지) */
export function hasSubstantialFixedEventContentElsewhere(
  eventName: string,
  candidateAnnualKey: string,
  programCells: Array<{
    grade_id: string;
    monday_iso: string;
    weekday_key: string;
    slot_index: number;
    unit_text?: string | null;
    learning_text?: string | null;
  }>,
  fixedRows: Array<{
    source_annual_cell_key?: string | null;
    unit_text?: string | null;
    learning_text?: string | null;
  }>
): boolean {
  for (const pc of programCells) {
    const k = buildAnnualCellKey(pc.grade_id, pc.monday_iso, pc.weekday_key, pc.slot_index);
    if (k === candidateAnnualKey) continue;
    if (fixedEventQueueHasSamePrefixLearningBody(pc, eventName)) return true;
  }
  for (const fr of fixedRows) {
    const k = (fr.source_annual_cell_key ?? '').trim();
    if (k === candidateAnnualKey) continue;
    if (fixedEventQueueHasSamePrefixLearningBody(fr, eventName)) return true;
  }
  return false;
}
